import React, { useState } from "react";
import { isMobile, isMobileOnly, isTablet } from "react-device-detect";
import styled, { css } from "styled-components";
import { FocusModeSidebarContentType } from "@lifeat/shared/system/AppModeTypes";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import GA4Event, { GA4EventProperties } from "@analytics/GoogleAnalytics/GA4Event";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import { VerticalDivider } from "@common/LifeAtDivider/LifeAtDivider";
import { useAppearanceContext } from "@contexts/AppearanceContext";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import SpacesButtonImage from "@resources/images/spaces-button-image.png";
import Styles from "@themes/Styles";
import { SignUpCTASource } from "@user/AmplitudeUserEvents";
import { ToggleWidgetEvent } from "../../AmplitudeWidgetToolbarEvents";
import { WidgetDataType, WidgetEntryType, WidgetName } from "../../WidgetDataManager";
import { useBreathingWidgetContext } from "../Widget/BreathingWidget/BreathingWidgetContext";
import ToolbarSettings from "./ToolbarSettings";
import WidgetToolbarElementV2 from "./WidgetToolbarElement";
import WidgetToolbarIcon from "./WidgetToolbarIcon";

const BottomNavContainer = styled.div<{ isHidden: boolean }>`
    position: fixed;
    z-index: ${Styles.zIndexWidgetContents};
    height: ${Styles.bottomNavigationHeight};
    width: 100%;
    display: flex;
    justify-content: center;
    margin-bottom: 8px;
    bottom: ${(props) => (props.isHidden ? `calc(-${Styles.bottomNavigationHeight} - 8px)` : 0)};
    transition: bottom 0.3s ease-out;
`;

const BottomNavContentContainer = styled.div`
    height: ${Styles.bottomNavigationHeight};
    width: fit-content;
    background-color: ${(props) => props.theme.backgroundPrimary};
    display: flex;
    align-items: center;
    border-radius: 13px;
    border: 1px solid ${(props) => props.theme.borderPrimary};
`;

const WidgetRowContainer = styled.div<{ hideScrollbar: boolean; isTouchDevice: boolean }>`
    padding: 0 15px;
    display: flex;
    align-items: center;
    gap: 10px;

    max-width: 270px;
    overflow-x: scroll;

    ${({ hideScrollbar, isTouchDevice }) =>
        !hideScrollbar &&
        css`
            ::-webkit-scrollbar-thumb {
                display: ${isTouchDevice ? `block` : `none`};
                border-radius: 8px;
                background-color: ${(props) => props.theme.controlPrimary};
                border: 2px ${(props) => props.theme.backgroundPrimary} solid;
            }
            ::-webkit-scrollbar-track {
                margin: 8px;
            }

            ::-webkit-scrollbar {
                display: block;
                height: 10px;
            }

            &:hover {
                ::-webkit-scrollbar-thumb,
                ::-webkit-scrollbar {
                    display: block;
                }
            }

            margin-top: 10px; //this is to account for the scrollbar on the bottom, so that the content is still centered
        `}
`;

const SpacesButton = styled.div`
    height: 100%;
    cursor: pointer;
    padding: 10px 10px 0 10px;
`;
const StyledSpacesButtonImage = styled.img`
    height: 100%;
`;

interface WidgetToolbarProps {
    widgetData: WidgetDataType;
    openWidgetKeys: Set<string>;
    toggleWidget: (id: string) => void;
    sidebarContent: FocusModeSidebarContentType;
    sidebarOpen: boolean;
    toggleSpacesSidebar: () => void;
    toggleCalendarSidebar: () => void;
    toggleSoundboardSidebar: () => void;
    closeAndResetAllWidgets: () => void;
}

/**
 * Bottom navigation toolbar with image button for spaces
 * @param props
 * @constructor
 */
export default function WidgetToolbarV1(props: WidgetToolbarProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const { user, userData }: UserContextType = useUserContext();
    const { setSignInView }: CallToActionContextType = useCallToActionContext();
    const { isFocusModeActive } = useAppearanceContext();
    const breathingWidget = useBreathingWidgetContext();

    const { SpacesWidget, TimerWidget, ...remainingWidgets } = props.widgetData;

    const [isSpacesWidgetOpen, setIsSpacesWidgetOpen] = useState(
        props.sidebarContent === "spaces" ? props.sidebarOpen : false,
    );

    const logToggleWidgetEvent = (widgetName: WidgetName) => {
        amplitudeService.logEvent(new ToggleWidgetEvent(ToggleWidgetEvent.SOURCES.TOOLBAR, true, widgetName));

        GA4Service.logEvent(GA4Event.TOGGLE_WIDGET, {
            [GA4EventProperties.widget_name]: widgetName,
            [GA4EventProperties.toggle_widget.key]: GA4EventProperties.toggle_widget.open,
        });
    };

    const getWidgetToolbarElement = (id: string, widgetEntry: WidgetEntryType | undefined) => {
        if (widgetEntry) {
            let handleClick = () => props.toggleWidget(id);
            let isOpen = false;
            if (widgetEntry.requireUserAccount && (!user?.emailVerified || !userData)) {
                // If the widget requires a user account, but there isn't one
                // created, do not allow the user to toggle the widget.
                handleClick = () =>
                    id === "CalendarWidget"
                        ? setSignInView(SignUpCTASource.CALENDAR_SIDEBAR)
                        : setSignInView(SignUpCTASource.TOOLBAR);
            } else {
                let openWidget: () => void;
                switch (id) {
                    case "SpacesWidget":
                        openWidget = () => props.toggleSpacesSidebar();
                        isOpen = props.sidebarContent === "spaces" ? props.sidebarOpen : false;
                        break;
                    case "CalendarWidget":
                        openWidget = () => props.toggleCalendarSidebar();
                        isOpen = props.sidebarContent === "calendar" ? props.sidebarOpen : false;
                        break;
                    case "SoundboardWidget":
                        openWidget = () => props.toggleSoundboardSidebar();
                        isOpen = props.sidebarContent === "soundboard" ? props.sidebarOpen : false;
                        break;
                    case "BreathingWidget":
                        openWidget = () => breathingWidget.open();
                        break;
                    default:
                        openWidget = () => props.toggleWidget(id);
                        isOpen = props.openWidgetKeys.has(id);
                }
                handleClick = () => {
                    if (!isOpen) {
                        logToggleWidgetEvent(widgetEntry.name);
                    }

                    openWidget();
                };
            }

            const icon = <WidgetToolbarIcon name={widgetEntry.name} icon={widgetEntry.icon} isOpen={isOpen} />;

            return (
                <WidgetToolbarElementV2
                    key={id}
                    id={id}
                    name={widgetEntry.name}
                    icon={icon}
                    isOpen={isOpen}
                    handleClick={handleClick}
                    requiresUserAccount={widgetEntry.requireUserAccount}
                    category={widgetEntry.category}
                />
            );
        } else {
            return null;
        }
    };

    const toolbarWidgets = Object.entries(remainingWidgets).map(([id, widgetEntry]: [string, WidgetEntryType]) => {
        if (widgetEntry.isAccessibleOnMobile || !isMobileOnly) {
            return getWidgetToolbarElement(id, widgetEntry);
        } else {
            return null;
        }
    });

    const timerButton = getWidgetToolbarElement("TimerWidget", TimerWidget);

    const handleToggleSpacesWidget = () => {
        props.toggleSpacesSidebar();
        setIsSpacesWidgetOpen(!isSpacesWidgetOpen);
        logToggleWidgetEvent(WidgetName.SPACES);
    };

    return (
        <BottomNavContainer isHidden={isFocusModeActive ?? false}>
            <BottomNavContentContainer>
                <SpacesButton onClick={handleToggleSpacesWidget}>
                    <StyledSpacesButtonImage src={SpacesButtonImage} alt="Spaces" />
                </SpacesButton>
                <VerticalDivider heightCssValue={"100%"} />
                <WidgetRowContainer hideScrollbar={isMobile} isTouchDevice={isMobile || isTablet}>
                    {timerButton}
                </WidgetRowContainer>
                <VerticalDivider heightCssValue={"100%"} />
                <WidgetRowContainer hideScrollbar={isMobile} isTouchDevice={isMobile || isTablet}>
                    {toolbarWidgets}
                    <ToolbarSettings closeAndResetAllWidgets={props.closeAndResetAllWidgets} />
                </WidgetRowContainer>
            </BottomNavContentContainer>
        </BottomNavContainer>
    );
}
